import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Minus, Plus, RotateCcw, Search, Send, Trash2, X } from "lucide-react";
import { CloseIconButton } from "@/components/ui/CloseIconButton";
import {
  useChangeRequestItems,
  useCreateChangeRequest,
  useUpdateChangeRequest,
} from "@/features/order/hooks";
import { useQuotationItems } from "@/features/quotation/hooks";
import {
  getQuotationBasePriceSuffix,
  getQuotationBasePriceValue,
  PRICE_PER_UNIT_SUFFIX,
} from "@/features/quotation/unitLabels";
import { getOrderMutationErrorMessage } from "@/features/order/errors";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import {
  formatQuantityDisplay,
  getDefaultQuantity,
  getQuantityInputMode,
  getQuantityInputStep,
  getWholeDirectKgQuantityInputAttributes,
  isDirectKgUnit,
  isLegacyDirectKgItem,
  parsePositiveQuantityInput,
  validateWholeDirectKgQuantityInput,
} from "@/lib/quantity";
import type {
  AddItem,
  ChangePayload,
  ChangeRequest,
  EnrichedOrder,
  EnrichedOrderItem,
  UpdateItem,
} from "@/features/order/types";

function formatBRL(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  }).format(value || 0);
}

function ModalItemRow({
  item,
  qty,
  removed,
  locked,
  onQtyChange,
  onToggleRemove,
}: {
  item: EnrichedOrderItem;
  qty: string;
  removed: boolean;
  locked: boolean;
  onQtyChange: (value: string) => void;
  onToggleRemove: () => void;
}) {
  const isWholeDirectKg =
    item.unit_type === "kg" && item.direct_kg_quantity_mode === "whole";
  const directKgValidation = isWholeDirectKg
    ? validateWholeDirectKgQuantityInput(qty)
    : null;
  const parsedQty = directKgValidation
    ? directKgValidation.quantity
    : parsePositiveQuantityInput(qty, item.unit_type);
  const changed = !removed && parsedQty !== item.quantity;
  const state = removed ? "removed" : changed ? "changed" : "default";

  return (
    <div className="change-request-modal-editor-row" data-state={state}>
      <div className="change-request-modal-item-copy">
        <div className="change-request-modal-product-line">
          <p className="change-request-modal-product">{item.product_name}</p>
          {changed && (
            <span className="change-request-modal-state-label">
              Quantidade alterada
            </span>
          )}
          {removed && (
            <span className="change-request-modal-state-label">Remover</span>
          )}
        </div>
        <p className="change-request-modal-meta">{item.supplier_name}</p>
        <p className="change-request-modal-value">
          Atual: {formatQuantityDisplay(item.quantity, item.unit_type)}
          {changed
            ? ` → Solicitado: ${formatQuantityDisplay(parsedQty, item.unit_type)}`
            : ""}
        </p>
        {locked && (
          <p className="change-request-modal-meta">
            Alteração já enviada. Para desfazê-la, cancele esta solicitação e
            crie outra.
          </p>
        )}
      </div>

      <div className="change-request-modal-quantity-controls">
        <input
          aria-label={`Quantidade de ${item.product_name}`}
          className="change-request-modal-quantity-input"
          type="number"
          {...(isWholeDirectKg
            ? getWholeDirectKgQuantityInputAttributes()
            : {
                min: getDefaultQuantity(item.unit_type),
                step: getQuantityInputStep(item.unit_type),
                inputMode: getQuantityInputMode(item.unit_type),
              })}
          value={qty}
          disabled={removed || locked}
          onChange={(event) => onQtyChange(event.target.value)}
          onBlur={() => {
            if (isWholeDirectKg) return;
            const value = parsePositiveQuantityInput(qty, item.unit_type);
            onQtyChange(
              formatQuantityDisplay(value ?? item.quantity, item.unit_type),
            );
          }}
        />

        <button
          className={`change-request-modal-icon-button ${
            removed ? "is-restore" : "is-danger"
          }`}
          type="button"
          onClick={onToggleRemove}
          disabled={locked}
          aria-label={removed ? "Restaurar" : "Remover"}
          title={
            locked
              ? "Alteração já enviada; cancele a solicitação para desfazê-la."
              : removed
                ? "Restaurar"
                : "Remover"
          }
        >
          {removed ? <RotateCcw size={17} /> : <Minus size={17} />}
        </button>
      </div>
    </div>
  );
}

function AddedItemRow({
  productName,
  supplierName,
  pricePerBox,
  pricePerKg,
  pricePerUnit,
  unitType,
  quantity,
  onChangeQty,
  onRemove,
}: {
  productName: string;
  supplierName: string;
  pricePerBox?: number;
  pricePerKg?: number;
  pricePerUnit?: number;
  unitType: string;
  quantity: string;
  onChangeQty: (quantity: string) => void;
  onRemove: () => void;
}) {
  const isNewDirectKg = isDirectKgUnit(unitType);
  return (
    <div className="change-request-modal-editor-row" data-state="added">
      <div className="change-request-modal-item-copy">
        <div className="change-request-modal-product-line">
          <p className="change-request-modal-product">{productName}</p>
          <span className="change-request-modal-state-label">Novo item</span>
        </div>
        <p className="change-request-modal-meta">{supplierName}</p>
        {pricePerUnit !== undefined && (
          <div className="change-request-modal-prices">
            <span>
              {formatBRL(
                getQuotationBasePriceValue({
                  unit_type: unitType as any,
                  price_per_box: pricePerBox,
                  price_per_kg: pricePerKg,
                }),
              )}{" "}
              / {getQuotationBasePriceSuffix(unitType as any)}
            </span>
            <span>
              {formatBRL(pricePerUnit)} /{" "}
              {PRICE_PER_UNIT_SUFFIX[
                unitType as keyof typeof PRICE_PER_UNIT_SUFFIX
              ] ?? unitType}
            </span>
          </div>
        )}
      </div>

      <div className="change-request-modal-quantity-controls">
        <input
          aria-label={`Quantidade de ${productName}`}
          className="change-request-modal-quantity-input"
          type="number"
          {...(isNewDirectKg
            ? getWholeDirectKgQuantityInputAttributes()
            : {
                min: getDefaultQuantity(unitType),
                step: getQuantityInputStep(unitType),
                inputMode: getQuantityInputMode(unitType),
              })}
          value={quantity}
          onChange={(event) => onChangeQty(event.target.value)}
          onBlur={(event) => {
            if (isNewDirectKg) return;
            const value = parsePositiveQuantityInput(
              event.target.value,
              unitType,
            );
            if (!value || value < getDefaultQuantity(unitType)) {
              onChangeQty(String(getDefaultQuantity(unitType)));
              return;
            }
            onChangeQty(formatQuantityDisplay(value, unitType));
          }}
          onFocus={(event) => event.target.select()}
        />

        <button
          className="change-request-modal-icon-button is-danger"
          type="button"
          onClick={onRemove}
          aria-label="Remover"
          title="Remover"
        >
          <Trash2 size={17} />
        </button>
      </div>
    </div>
  );
}

export default function ChangeRequestModal({
  order,
  existingCR,
  onClose,
  onFeedback,
}: {
  order: EnrichedOrder;
  existingCR?: ChangeRequest;
  onClose: () => void;
  onFeedback?: (feedback: {
    title: string;
    message?: string;
    tone: "success" | "error";
  }) => void;
}) {
  const [isClosing, setIsClosing] = useState(false);
  const closingRef = useRef(false);
  const closeTimerRef = useRef<number | null>(null);
  const mountedRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const submittingRef = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  onCloseRef.current = onClose;
  const hasLegacyDirectKg = order.items.some(isLegacyDirectKgItem);
  const availableItems = order.items.filter(
    (item) => item.is_available !== false && !isLegacyDirectKgItem(item),
  );

  const [quantities, setQuantities] = useState<Record<string, string>>(() => {
    const map = Object.fromEntries(
      availableItems.map((item) => [
        item.id,
        formatQuantityDisplay(item.quantity, item.unit_type),
      ]),
    );
    existingCR?.changes_payload.update?.forEach((update) => {
      if (update.quantity !== undefined) {
        const item = availableItems.find(
          (entry) => entry.id === update.order_item_id,
        );
        map[update.order_item_id] = formatQuantityDisplay(
          update.quantity,
          item?.unit_type,
        );
      }
    });
    return map;
  });
  const [removed, setRemoved] = useState<Set<string>>(
    () => new Set(existingCR?.changes_payload.remove ?? []),
  );
  const [addedItems, setAddedItems] = useState<AddItem[]>(
    () => existingCR?.changes_payload.add ?? [],
  );
  const [addedQuantityInputs, setAddedQuantityInputs] = useState<
    Record<string, string>
  >(() =>
    Object.fromEntries(
      (existingCR?.changes_payload.add ?? []).map((item) => [
        item.quotation_item_id,
        String(item.quantity),
      ]),
    ),
  );
  const persistedRemoved = new Set(existingCR?.changes_payload.remove ?? []);
  const persistedUpdated = new Set(
    (existingCR?.changes_payload.update ?? []).map(
      (item) => item.order_item_id,
    ),
  );
  const [addSearch, setAddSearch] = useState("");
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCR = useCreateChangeRequest();
  const updateCR = useUpdateChangeRequest();
  const isPending = createCR.isPending || updateCR.isPending;

  const quotationId = order.quotation_id ?? "";
  const { data: quotationItemsData, isError: quotationItemsUnavailable } =
    useQuotationItems(quotationId, order.store_id);
  const quotationItems = quotationItemsData?.items ?? [];
  const { data: historicalItems = [] } = useChangeRequestItems(existingCR?.id);
  const existingQItemIds = new Set(
    order.items.map((item) => item.quotation_item_id),
  );
  const addedQItemIds = new Set(
    addedItems.map((item) => item.quotation_item_id),
  );
  const availableToAdd = quotationItems.filter(
    (item) => !existingQItemIds.has(item.id) && !addedQItemIds.has(item.id),
  );
  const filteredAvailable = addSearch.trim()
    ? availableToAdd.filter(
        (item) =>
          item.product_name.toLowerCase().includes(addSearch.toLowerCase()) ||
          item.supplier_name.toLowerCase().includes(addSearch.toLowerCase()),
      )
    : availableToAdd;

  function requestClose() {
    if (!mountedRef.current || closingRef.current) return;
    closingRef.current = true;
    setIsClosing(true);
    const closeDelay = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? 0
      : 140;
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      if (mountedRef.current) onCloseRef.current();
    }, closeDelay);
  }

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    const previousInert = root.inert;
    root.inert = true;
    return () => {
      root.inert = previousInert;
    };
  }, []);

  useDialogFocus(dialogRef, requestClose);

  function buildPayload(): ChangePayload {
    const updates: UpdateItem[] = [];
    const removes: string[] = [];
    const additions: AddItem[] = [];
    const removedAdditions: string[] = [];
    const initialAdditions = new Map(
      (existingCR?.changes_payload.add ?? []).map((item) => [
        item.quotation_item_id,
        item,
      ]),
    );

    for (const item of availableItems) {
      if (removed.has(item.id)) {
        removes.push(item.id);
        continue;
      }

      const rawQuantity = quantities[item.id] ?? String(item.quantity);
      const directKgValidation =
        item.unit_type === "kg" && item.direct_kg_quantity_mode === "whole"
          ? validateWholeDirectKgQuantityInput(rawQuantity)
          : null;
      if (directKgValidation?.reason) continue;
      const parsedQty = directKgValidation
        ? directKgValidation.quantity
        : Math.max(
            getDefaultQuantity(item.unit_type),
            parsePositiveQuantityInput(rawQuantity, item.unit_type) ??
              item.quantity,
          );
      if (parsedQty !== item.quantity) {
        updates.push({ order_item_id: item.id, quantity: parsedQty });
      }
    }

    for (const item of addedItems) {
      const quotationItem =
        quotationItems.find((entry) => entry.id === item.quotation_item_id) ??
        historicalItems.find((entry) => entry.id === item.quotation_item_id);
      const rawQuantity =
        addedQuantityInputs[item.quotation_item_id] ?? String(item.quantity);
      const directKgValidation = isDirectKgUnit(quotationItem?.unit_type)
        ? validateWholeDirectKgQuantityInput(rawQuantity)
        : null;
      if (directKgValidation?.reason) continue;
      const addition = {
        ...item,
        quantity: directKgValidation
          ? directKgValidation.quantity
          : (parsePositiveQuantityInput(
              rawQuantity,
              quotationItem?.unit_type,
            ) ?? item.quantity),
      };
      const initial = initialAdditions.get(item.quotation_item_id);
      if (
        initial &&
        initial.quantity === addition.quantity &&
        initial.final_price === addition.final_price
      ) {
        continue;
      }
      if (initial) removedAdditions.push(item.quotation_item_id);
      additions.push(addition);
    }

    initialAdditions.forEach((_item, quotationItemId) => {
      if (!addedQItemIds.has(quotationItemId)) {
        removedAdditions.push(quotationItemId);
      }
    });

    return {
      add: additions.length > 0 ? additions : undefined,
      remove_add: removedAdditions.length > 0 ? removedAdditions : undefined,
      remove: removes.length > 0 ? removes : undefined,
      update: updates.length > 0 ? updates : undefined,
    };
  }

  function buildPayloadCounts() {
    const payload = buildPayload();
    return {
      adds: payload.add?.length ?? 0,
      updates: payload.update?.length ?? 0,
      removes: payload.remove?.length ?? 0,
    };
  }

  function hasChanges() {
    const payload = buildPayload();
    if (existingCR) {
      return (
        (payload.add?.length ?? 0) > 0 ||
        (payload.remove_add?.length ?? 0) > 0 ||
        JSON.stringify(payload.remove ?? []) !==
          JSON.stringify(existingCR.changes_payload.remove ?? []) ||
        JSON.stringify(payload.update ?? []) !==
          JSON.stringify(existingCR.changes_payload.update ?? [])
      );
    }
    const adds = payload.add?.length ?? 0;
    const updates = payload.update?.length ?? 0;
    const removes = payload.remove?.length ?? 0;
    return (
      adds > 0 ||
      updates > 0 ||
      removes > 0 ||
      availableItems.some(
        (item) =>
          item.unit_type === "kg" &&
          item.direct_kg_quantity_mode === "whole" &&
          (quantities[item.id] ?? String(item.quantity)) !==
            formatQuantityDisplay(item.quantity, item.unit_type),
      )
    );
  }

  function hasInvalidWholeDirectKgQuantity() {
    const invalidExisting = availableItems.some(
      (item) =>
        item.unit_type === "kg" &&
        item.direct_kg_quantity_mode === "whole" &&
        validateWholeDirectKgQuantityInput(
          quantities[item.id] ?? String(item.quantity),
        ).reason,
    );
    if (invalidExisting) return true;

    return addedItems.some((item) => {
      const quotationItem =
        quotationItems.find((entry) => entry.id === item.quotation_item_id) ??
        historicalItems.find((entry) => entry.id === item.quotation_item_id);
      return (
        isDirectKgUnit(quotationItem?.unit_type) &&
        Boolean(
          validateWholeDirectKgQuantityInput(
            addedQuantityInputs[item.quotation_item_id] ??
              String(item.quantity),
          ).reason,
        )
      );
    });
  }

  function handleAddQuotationItem(quotationItemId: string) {
    const unitType = quotationItems.find(
      (item) => item.id === quotationItemId,
    )?.unit_type;
    const initialQuantity = isDirectKgUnit(unitType)
      ? 1
      : getDefaultQuantity(unitType);
    setAddedItems((current) => [
      ...current,
      {
        quotation_item_id: quotationItemId,
        quantity: initialQuantity,
      },
    ]);
    setAddedQuantityInputs((current) => ({
      ...current,
      [quotationItemId]: String(initialQuantity),
    }));
    setAddSearch("");
  }

  function handleUpdateAddedQty(quotationItemId: string, quantity: string) {
    setAddedQuantityInputs((current) => ({
      ...current,
      [quotationItemId]: quantity,
    }));
  }

  function handleRemoveAdded(quotationItemId: string) {
    setAddedItems((current) =>
      current.filter((item) => item.quotation_item_id !== quotationItemId),
    );
    setAddedQuantityInputs((current) => {
      const next = { ...current };
      delete next[quotationItemId];
      return next;
    });
  }

  async function handleSubmit() {
    if (
      !mountedRef.current ||
      closingRef.current ||
      submittingRef.current ||
      !hasChanges()
    ) {
      return;
    }
    if (hasInvalidWholeDirectKgQuantity()) {
      const feedback = {
        title: "Quantidade inválida",
        message:
          "Para itens vendidos por kg, informe um número inteiro maior que zero.",
        tone: "error" as const,
      };
      onFeedback?.(feedback);
      if (!onFeedback) setError(feedback.title);
      return;
    }
    submittingRef.current = true;
    setError(null);

    try {
      if (existingCR) {
        await updateCR.mutateAsync({
          crId: existingCR.id,
          payload: { changes_payload: buildPayload() },
        });
      } else {
        await createCR.mutateAsync({
          orderId: order.id,
          payload: { changes_payload: buildPayload() },
        });
      }
      if (mountedRef.current) requestClose();
    } catch (caughtError) {
      if (mountedRef.current) {
        const message = getOrderMutationErrorMessage(caughtError);
        onFeedback?.({ title: message, tone: "error" });
        setError(message);
      }
    } finally {
      submittingRef.current = false;
    }
  }

  const { adds, updates, removes } = buildPayloadCounts();
  const summaryParts = [
    updates > 0 && `${updates} ${updates === 1 ? "alteração" : "alterações"}`,
    removes > 0 && `${removes} ${removes === 1 ? "remoção" : "remoções"}`,
    adds > 0 && `${adds} ${adds === 1 ? "adição" : "adições"}`,
  ].filter(Boolean);

  return createPortal(
    <div
      className="change-request-modal-overlay"
      data-state={isClosing ? "closing" : "open"}
      onClick={requestClose}
    >
      <div
        ref={dialogRef}
        className="change-request-modal-panel"
        data-state={isClosing ? "closing" : "open"}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={
          existingCR
            ? "Editar solicitação de alteração"
            : "Solicitar alterações"
        }
        tabIndex={-1}
      >
        <header className="change-request-modal-header">
          <div className="change-request-modal-heading">
            <h2 className="change-request-modal-title">
              {existingCR ? "Editar solicitação" : "Solicitar alterações"}
            </h2>
            <p className="change-request-modal-store">{order.store_name}</p>
          </div>

          <CloseIconButton
            label="Fechar"
            iconSize={19}
            className="change-request-modal-close"
            onClick={requestClose}
          />

          <div className="change-request-modal-counts" aria-live="polite">
            <span>
              {availableItems.length}{" "}
              {availableItems.length === 1
                ? "item disponível"
                : "itens disponíveis"}
            </span>
            {summaryParts.length > 0 && <span>{summaryParts.join(" • ")}</span>}
          </div>
        </header>

        <div className="change-request-modal-body">
          {hasLegacyDirectKg ? (
            <p className="change-request-modal-error" role="status">
              Registro histórico — somente visualização.
            </p>
          ) : (
            <section className="change-request-modal-section">
              <h3 className="change-request-modal-section-title">
                Itens do pedido
              </h3>

              <div className="change-request-modal-editor">
                {availableItems.map((item) => (
                  <ModalItemRow
                    key={item.id}
                    item={item}
                    qty={quantities[item.id] ?? String(item.quantity)}
                    removed={removed.has(item.id)}
                    locked={
                      persistedRemoved.has(item.id) ||
                      persistedUpdated.has(item.id)
                    }
                    onQtyChange={(value) =>
                      setQuantities((current) => ({
                        ...current,
                        [item.id]: value,
                      }))
                    }
                    onToggleRemove={() =>
                      setRemoved((current) => {
                        const next = new Set(current);
                        if (next.has(item.id)) next.delete(item.id);
                        else next.add(item.id);
                        return next;
                      })
                    }
                  />
                ))}
              </div>

              {order.items.some((item) => item.is_available === false) && (
                <div className="change-request-modal-unavailable">
                  <h4 className="change-request-modal-subsection-title">
                    Itens indisponíveis
                  </h4>
                  {order.items
                    .filter((item) => item.is_available === false)
                    .map((item) => (
                      <div
                        className="change-request-modal-unavailable-row"
                        key={item.id}
                      >
                        <div className="change-request-modal-item-copy">
                          <p className="change-request-modal-product">
                            {item.product_name}
                          </p>
                          <p className="change-request-modal-meta">
                            {item.supplier_name}
                          </p>
                        </div>
                        <span className="change-request-modal-state-label">
                          Indisponível
                        </span>
                      </div>
                    ))}
                </div>
              )}
            </section>
          )}

          {!hasLegacyDirectKg && addedItems.length > 0 && (
            <section className="change-request-modal-section">
              <h3 className="change-request-modal-section-title">
                Itens adicionados
              </h3>
              <div className="change-request-modal-editor">
                {addedItems.map((addedItem) => {
                  const quotationItem =
                    quotationItems.find(
                      (item) => item.id === addedItem.quotation_item_id,
                    ) ??
                    historicalItems.find(
                      (item) => item.id === addedItem.quotation_item_id,
                    );
                  if (!quotationItem) return null;

                  return (
                    <AddedItemRow
                      key={addedItem.quotation_item_id}
                      productName={quotationItem.product_name}
                      supplierName={quotationItem.supplier_name}
                      pricePerBox={quotationItem.price_per_box}
                      pricePerKg={quotationItem.price_per_kg}
                      pricePerUnit={quotationItem.price_per_unit}
                      unitType={quotationItem.unit_type}
                      quantity={
                        addedQuantityInputs[addedItem.quotation_item_id] ??
                        String(addedItem.quantity)
                      }
                      onChangeQty={(quantity) =>
                        handleUpdateAddedQty(
                          addedItem.quotation_item_id,
                          quantity,
                        )
                      }
                      onRemove={() =>
                        handleRemoveAdded(addedItem.quotation_item_id)
                      }
                    />
                  );
                })}
              </div>
            </section>
          )}

          {!hasLegacyDirectKg && quotationId && (
            <section className="change-request-modal-add-area">
              <button
                className="change-request-modal-add-toggle"
                type="button"
                onClick={() => setShowAddPanel((current) => !current)}
                aria-expanded={showAddPanel}
                disabled={quotationItemsUnavailable}
                title={
                  quotationItemsUnavailable
                    ? "A cotação publicada não está disponível para novas adições."
                    : undefined
                }
              >
                <Plus size={17} />
                {showAddPanel
                  ? "Fechar busca de itens"
                  : "Adicionar item à solicitação"}
              </button>

              {showAddPanel && !quotationItemsUnavailable && (
                <div className="change-request-modal-add-panel">
                  <div className="change-request-modal-search">
                    <Search size={17} aria-hidden="true" />
                    <input
                      className="change-request-modal-search-input"
                      type="text"
                      aria-label="Buscar produto ou fornecedor"
                      placeholder="Buscar produto ou fornecedor"
                      value={addSearch}
                      onChange={(event) => setAddSearch(event.target.value)}
                      autoFocus
                    />
                    {addSearch && (
                      <button
                        className="change-request-modal-search-clear"
                        type="button"
                        onClick={() => setAddSearch("")}
                        aria-label="Limpar busca"
                      >
                        <X size={17} />
                      </button>
                    )}
                  </div>

                  <div className="change-request-modal-results">
                    {filteredAvailable.length === 0 ? (
                      <p className="change-request-modal-results-empty">
                        {availableToAdd.length === 0
                          ? "Todos os itens desta cotação já estão no pedido."
                          : "Nenhum item encontrado para esse filtro."}
                      </p>
                    ) : (
                      filteredAvailable.map((item) => (
                        <button
                          className="change-request-modal-result"
                          type="button"
                          key={item.id}
                          onClick={() => handleAddQuotationItem(item.id)}
                        >
                          <span className="change-request-modal-result-copy">
                            <span className="change-request-modal-product">
                              {item.product_name}
                            </span>
                            <span className="change-request-modal-meta">
                              {item.supplier_name}
                            </span>
                            <span className="change-request-modal-result-prices">
                              <span>
                                {formatBRL(
                                  getQuotationBasePriceValue(item as any),
                                )}{" "}
                                /{" "}
                                {getQuotationBasePriceSuffix(
                                  item.unit_type as any,
                                )}
                              </span>
                              <span>
                                {formatBRL(item.price_per_unit)} /{" "}
                                {PRICE_PER_UNIT_SUFFIX[
                                  item.unit_type as keyof typeof PRICE_PER_UNIT_SUFFIX
                                ] ?? item.unit_type}
                              </span>
                            </span>
                          </span>
                          <span className="change-request-modal-result-action">
                            <Plus size={16} />
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <footer className="change-request-modal-footer">
          {error && (
            <p className="change-request-modal-error" role="alert">
              {error}
            </p>
          )}
          <div className="change-request-modal-footer-actions">
            <button
              className="change-request-modal-button is-neutral"
              type="button"
              onClick={requestClose}
            >
              Cancelar
            </button>
            {!hasLegacyDirectKg && (
              <button
                className="change-request-modal-button is-primary"
                type="button"
                onClick={handleSubmit}
                disabled={!hasChanges() || isPending}
              >
                <Send size={17} />
                {isPending
                  ? "Enviando..."
                  : existingCR
                    ? "Atualizar solicitação"
                    : "Enviar solicitação"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
